import { supabase } from '@/integrations/supabase/client';
import { testBasicConnection } from './connectionCheck';
import { testStorageBuckets } from './storageCheck';
import { testDatabaseAccess } from './databaseAccessCheck';
import { 
    testProfileService, 
    testMessageService, 
    testBookingService 
} from './serviceChecks';

// Re-export individual checks if needed
export * from './connectionCheck';
export * from './storageCheck';
export * from './databaseAccessCheck';
export * from './serviceChecks';

/**
 * Runs all Supabase health checks sequentially.
 * @returns A promise that resolves to an object containing the results of all checks.
 */
export const verifySupabaseConnections = async () => {
  console.log('Health Orchestrator: Starting comprehensive Supabase verification...');
  
  const results: Record<string, any> = {
    overall: {
      success: true,
      message: 'All Supabase checks passed.',
      status: 'success', // Add a status field
      details: {}
    },
    checks: {} // Store individual check results here
  };
  
  const runCheck = async (name: string, checkFn: () => Promise<any>) => {
      console.log(`--- Running check: ${name} ---`);
      const result = await checkFn();
      results.checks[name] = result;
      if (!result.success) {
          results.overall.success = false;
          results.overall.status = 'error'; // Set overall status to error if any check fails
          results.overall.message = `Check failed: ${name}. ${result.message || ''}`;
          console.error(`--- Check FAILED: ${name} ---`);
      } else {
          console.log(`--- Check PASSED: ${name} ---`);
      }
      return result.success;
  };

  try {
    await runCheck('basicConnection', testBasicConnection);
    // Only continue if basic connection works
    if (results.overall.success) { 
        await runCheck('databaseAccess', testDatabaseAccess);
        await runCheck('storage', testStorageBuckets);
        await runCheck('profileService', testProfileService);
        await runCheck('messageService', testMessageService);
        await runCheck('bookingService', testBookingService);
    }
    
    results.timestamp = new Date().toISOString();
    const checkKeys = Object.keys(results.checks);
    const passedChecks = checkKeys.filter(key => results.checks[key].success).length;
    results.overall.details = {
      testsRun: checkKeys.length,
      testsPassed: passedChecks,
      // Add any other relevant summary info here
    };
    
    if(results.overall.success) {
        results.overall.message = `All ${checkKeys.length} checks passed successfully.`;
    }

    console.log('Health Orchestrator: Supabase verification completed.');
    
  } catch (error) {
    console.error('Health Orchestrator: Critical error during verification sequence:', error);
    results.overall = {
        success: false,
        status: 'critical_error',
        message: `Verification failed with critical error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        error: error instanceof Error ? error.stack : String(error),
        details: {}
    };
  } finally {
      results.timestamp = results.timestamp || new Date().toISOString();
  }

  return results;
};

/**
 * Generates a simple string report from the verification results.
 * @param results - The results object from verifySupabaseConnections.
 * @returns A formatted string report.
 */
export const generateSupabaseHealthReport = (results: Record<string, any>): string => {
  let report = `Supabase Health Report (${new Date(results.timestamp || Date.now()).toLocaleString()})\n`;
  report += `==================================================\n`;
  report += `Overall Status: ${results.overall?.success ? '✅ SUCCESS' : '❌ FAILED'}\n`;
  report += `Message: ${results.overall?.message || 'N/A'}\n`;
  if (results.overall?.details) {
      report += `Details: ${results.overall.details.testsPassed} / ${results.overall.details.testsRun} checks passed.\n`;
  }
  report += `--------------------------------------------------\n`;

  if (results.checks && typeof results.checks === 'object') {
      for (const [key, checkResult] of Object.entries(results.checks)) {
          report += `\n[${key}]\n`;
          const isSuccess = typeof checkResult === 'object' && checkResult !== null && 'success' in checkResult ? checkResult.success : false;
          const message = typeof checkResult === 'object' && checkResult !== null && 'message' in checkResult ? checkResult.message : 'No message';
          const errorMsg = typeof checkResult === 'object' && checkResult !== null && 'error' in checkResult ? checkResult.error : null;
          
          report += `  Status: ${isSuccess ? '✅ Passed' : '❌ Failed'}\n`;
          if (message) {
              report += `  Message: ${message}\n`;
          }
          if (errorMsg) {
              report += `  Error: ${errorMsg}\n`;
          }
          const accessResults = typeof checkResult === 'object' && checkResult !== null && 'accessResults' in checkResult ? checkResult.accessResults : null;
          
          if (key === 'storage' && accessResults && typeof accessResults === 'object') {
               report += `  Bucket Access Details:\n`;
               for (const [bucket, access] of Object.entries(accessResults as Record<string, any>)) {
                   const bucketExists = typeof access === 'object' && access !== null && 'exists' in access ? access.exists : false;
                   const bucketSuccess = typeof access === 'object' && access !== null && 'success' in access ? access.success : false;
                   const bucketError = typeof access === 'object' && access !== null && 'error' in access ? access.error : 'reason unknown';
                   report += `    - ${bucket}: ${bucketExists ? (bucketSuccess ? 'Accessible' : `Inaccessible (${bucketError})`) : 'Missing'}\n`;
               }
          }
           if (key === 'databaseAccess' && accessResults && typeof accessResults === 'object') {
               report += `  Table Access Details:\n`;
               for (const [table, access] of Object.entries(accessResults as Record<string, any>)) {
                   const tableSuccess = typeof access === 'object' && access !== null && 'success' in access ? access.success : false;
                   const tableError = typeof access === 'object' && access !== null && 'error' in access ? access.error : 'reason unknown';
                   report += `    - ${table}: ${tableSuccess ? 'Accessible' : `Inaccessible (${tableError})`}\n`;
               }
          }
      }
  }
  
  report += `==================================================\n`;
  return report;
}; 